import { Campo, Entrada, Selecao } from '../componentes/ui';
import type { RascunhoSociedade } from './sociedade-nova-comum';
import { LinhaCheia, Secao } from './sociedade-nova-secao';

/**
 * O REGIME DO ESTATUTO — só na S.A.
 *
 * ## O que esta seção decide
 * Sete campos que a Lei 6.404/76 manda o estatuto declarar, e que uma limitada
 * não tem: se a companhia é aberta, até onde a administração pode emitir sem
 * assembleia, se há Conselho de Administração, se o Conselho Fiscal é
 * permanente, quanto do lucro vai obrigatoriamente aos acionistas, e o que
 * torna preferencial uma ação preferencial.
 *
 * ## Por que os padrões já vêm preenchidos
 * Companhia fechada, sem conselho, conselho fiscal não permanente, dividendo
 * de 25% — é o que a lei prevê quando o estatuto silencia, e é a companhia que
 * um contador de escritório abre. Quem precisa de outra coisa muda o campo;
 * quem não precisa não decide nada. O contrário — campos vazios que o
 * validador depois reprova — transformaria o padrão legal em trabalho.
 */
export function SecaoAcoes({
  rascunho: r,
  mudar,
}: {
  rascunho: RascunhoSociedade;
  mudar: (p: Partial<RascunhoSociedade>) => void;
}): JSX.Element | null {
  const a = r.acoes;
  if (!a) return null;

  const mexer = (parcial: Partial<NonNullable<RascunhoSociedade['acoes']>>) =>
    mudar({ acoes: { ...a, ...parcial } });

  const temPreferencial = r.socios.some((s) => s.especie === 'PREFERENCIAL');

  return (
    <Secao
      titulo="Estatuto da companhia"
      descricao="O que a Lei 6.404/76 exige que o estatuto declare. Os padrões abaixo são os da própria lei."
    >
      <Campo
        rotulo="Espécie de companhia"
        id="acoes-aberta"
        obrigatorio
        dica="Aberta negocia em bolsa e exige registro na CVM."
      >
        <Selecao
          id="acoes-aberta"
          value={a.aberta ? 'ABERTA' : 'FECHADA'}
          onChange={(e) => {
            const aberta = e.target.value === 'ABERTA';
            // Aberta exige Conselho de Administração (art. 138, §2º). Marcar
            // aqui evita que a pessoa escolha "aberta" e só descubra a
            // obrigação no laudo, com o cliente esperando.
            mexer({ aberta, ...(aberta ? { conselhoAdministracao: true } : {}) });
          }}
        >
          <option value="FECHADA">Fechada</option>
          <option value="ABERTA">Aberta (bolsa ou balcão)</option>
        </Selecao>
      </Campo>

      <Campo
        rotulo="Capital autorizado (R$)"
        id="acoes-autorizado"
        dica="Vazio = sem autorização; toda emissão passa por assembleia."
      >
        <Entrada
          id="acoes-autorizado"
          inputMode="decimal"
          value={a.capitalAutorizado}
          onChange={(e) => mexer({ capitalAutorizado: e.target.value })}
        />
      </Campo>

      <Campo
        rotulo="Dividendo mínimo obrigatório (%)"
        id="acoes-dividendo"
        obrigatorio
        dica="25% é o mínimo legal quando o estatuto silencia."
      >
        <Entrada
          id="acoes-dividendo"
          inputMode="decimal"
          value={a.dividendoMinimo}
          onChange={(e) => mexer({ dividendoMinimo: e.target.value })}
        />
      </Campo>

      <Campo rotulo="Conselho de Administração" id="acoes-conselho" obrigatorio>
        <Selecao
          id="acoes-conselho"
          value={a.conselhoAdministracao ? 'SIM' : 'NAO'}
          // Numa aberta o conselho é obrigatório: o campo fica travado em vez
          // de aceitar uma escolha que o laudo recusaria em seguida.
          disabled={a.aberta}
          onChange={(e) => mexer({ conselhoAdministracao: e.target.value === 'SIM' })}
        >
          <option value="NAO">Não há</option>
          <option value="SIM">Há conselho</option>
        </Selecao>
      </Campo>

      {a.conselhoAdministracao && (
        <Campo
          rotulo="Nº de conselheiros"
          id="acoes-conselheiros"
          obrigatorio
          dica="Mínimo de 3 (art. 140)."
        >
          <Entrada
            id="acoes-conselheiros"
            inputMode="numeric"
            value={a.conselheiros}
            onChange={(e) => mexer({ conselheiros: e.target.value })}
          />
        </Campo>
      )}

      <Campo
        rotulo="Conselho Fiscal"
        id="acoes-fiscal"
        obrigatorio
        dica="O estatuto tem de dizer qual dos dois (art. 161)."
      >
        <Selecao
          id="acoes-fiscal"
          value={a.conselhoFiscalPermanente ? 'PERMANENTE' : 'NAO_PERMANENTE'}
          onChange={(e) =>
            mexer({ conselhoFiscalPermanente: e.target.value === 'PERMANENTE' })
          }
        >
          <option value="NAO_PERMANENTE">Não permanente (instalado a pedido)</option>
          <option value="PERMANENTE">Permanente</option>
        </Selecao>
      </Campo>

      {/*
        Só aparece quando há preferenciais. É a vantagem que TORNA a ação
        preferencial — sem ela a ação seria ordinária sem voto, que a lei não
        admite —, e pedi-la a quem só tem ordinárias seria pedir a declaração
        de uma coisa que não existe no quadro.
      */}
      {temPreferencial && (
        <LinhaCheia>
          <Campo
            rotulo="Vantagem das ações preferenciais"
            id="acoes-vantagem"
            obrigatorio
            dica="Ex.: dividendo 10% maior que o das ordinárias. Vazio = prioridade no reembolso do capital."
          >
            <Entrada
              id="acoes-vantagem"
              value={a.vantagemPreferenciais}
              onChange={(e) => mexer({ vantagemPreferenciais: e.target.value })}
            />
          </Campo>
        </LinhaCheia>
      )}
    </Secao>
  );
}
